/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../../pages/styles/Integrations.module.scss';
import Toggle from '../../ui/Toggle/Toggle';
import Icon from '../../ui/Icon/Icon';
import Image from '../../ui/Image/Image';
import CountriesListModal from '../../modals/CountriesListModal/CountriesListModal';
import ConfirmationModal from '../../modals/ConfirmationModal/ConfirmationModal';
import TermsModal from '../../modals/TermsModal/TermsModal';
import ActivationSuccessfulModal from '../../modals/ActivationSuccessfulModal/ActivationSuccessfulModal';
import { CarrierCompany, useCarrierUpdate } from '../../../services/carrier';

type CarrierProps = CarrierCompany & {
  allowEdition?: boolean;
};

const CarrierCard: React.FC<CarrierProps> = ({
  id,
  name,
  recommended,
  region_ids,
  description,
  carrier_company_code,
  active,
  logo_url,
  allowEdition,
}) => {
  const { t } = useTranslation();
  const numericId = Number(id);

  if (!id && Number.isNaN(numericId)) {
    throw new Error(`Invalid carrier ID: ${id}`);
  }
  const [isCountriesModalOpen, setIsCountriesModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isReadonlyTermsModalOpen, setIsReadonlyTermsModalOpen] = useState(false);
  const [isActivationSuccessModalOpen, setIsActivationSuccessModalOpen] = useState(false);
  const [isActive, setIsActive] = useState(active);

  const mutation = useCarrierUpdate();

  const setCarrierActive = (id: number, setActive: boolean) => {
    mutation.mutate(
      {
        params: {
          path: {
            carrier_company_id: id,
            set_active: setActive,
          },
        },
      },
      {
        onSuccess: () => {
          setIsTermsModalOpen(false);
          if (setActive) {
            setTimeout(() => {
              setIsActivationSuccessModalOpen(true);
            }, 500);
          }
        },
      },
    );
  };

  const handleToggle = () => {
    if (isActive) {
      setIsDeactivateModalOpen(true);
    } else {
      setIsTermsModalOpen(true);
    }
  };

  const handleDeactivateConfirm = (confirmed: boolean) => {
    setIsDeactivateModalOpen(false);
    if (confirmed) {
      setCarrierActive(numericId, false);
      setIsActive(false);
    }
  };

  const handleConfirmTerms = (confirmed: boolean) => {
    if (confirmed) {
      setCarrierActive(numericId, true);
      setIsActive(true);
    } else {
      setIsTermsModalOpen(false);
    }
  };

  return (
    <div className={styles.integrationCard}>
      <div className="flex flex-col gap-[12px] p-20 border-subtle-bottom flex-1">
        <div className="flex justify-between items-start w-full">
          <Image
            imageUrl={logo_url ?? ''}
            alt={carrier_company_code ?? 'carrier'}
            className="w-[40px] h-[40px] rounded-xl"
            fallbackComponent={<Icon name="package" width={20} height={20} />}
          />
          {recommended && (
            <div className="flex justify-center items-center h-[20px] max-w-[130px] w-full rounded-lg gap-4 bg-[#DD962B1F] status-warning-full">
              <Icon name="lightning" width={12} height={12} />
              <span className="typo-text-12--bold">{t('carrier_analytics.recommendation')}</span>
            </div>
          )}
        </div>
        <div className="flex items-center">
          <h3 className="typo-text-16--bold">{name}</h3>
        </div>
        <p className="typo-text-12 text-faded text-ellipsis line-clamp-2 min-h-[32px]">{description}</p>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsCountriesModalOpen(true)}
          className="flex justify-self-end items-center gap-[4px] status-neutral-4-full cursor-pointer">
          <img className="w-[14px] h-[14px]" src="/planet.svg" alt="planet-icon" />
          <span className="typo-text-12">{t('carrier_analytics.view_countries')}</span>
        </div>
      </div>
      <div className="p-20 flex justify-between items-center w-full">
        <div className="mr-20">
          <Toggle disabled={!allowEdition} checked={isActive} onToggle={handleToggle} />
        </div>
        <div role="button" tabIndex={0} onClick={() => setIsReadonlyTermsModalOpen(true)} className="cursor-pointer">
          <span className="typo-text-12 text-faded">{t('carrier_analytics.terms_of_usage')}</span>
        </div>
      </div>
      <CountriesListModal
        isOpen={isCountriesModalOpen}
        onClose={() => setIsCountriesModalOpen(false)}
        items={region_ids || []}
      />
      <ConfirmationModal
        title={`${t('carrier_analytics.deactivate')} ${name}`}
        description={t('carrier_analytics.deactivate_confirm')}
        isOpen={isDeactivateModalOpen}
        onClose={handleDeactivateConfirm}
      />
      <ActivationSuccessfulModal
        title={t('carrier_analytics.activation_successful')}
        imageSrc="/assets/carriers/illustration.svg"
        isOpen={isActivationSuccessModalOpen}
        onClose={() => setIsActivationSuccessModalOpen(false)}
      />
      <TermsModal isOpen={isTermsModalOpen} onClose={handleConfirmTerms} />
      {isReadonlyTermsModalOpen && (
        <TermsModal readOnly isOpen={isReadonlyTermsModalOpen} onClose={() => setIsReadonlyTermsModalOpen(false)} />
      )}
    </div>
  );
};

export default CarrierCard;
